import React, { createContext, useContext, useReducer, useEffect, useState } from 'react';
import { collection, deleteDoc, doc, getDocs, setDoc, serverTimestamp, onSnapshot, updateDoc } from 'firebase/firestore';
import { logError } from '../utils/errorHandler';
import { useAuth } from './AuthContext';
import { db } from '../firebase/config';

const OrderContext = createContext();
const VAT_RATE = 0.075;

const orderReducer = (state, action) => {
  switch (action.type) {
    case 'ADD_ORDER':
      return {
        ...state,
        orders: [action.payload, ...state.orders]
      };

    case 'LOAD_ORDERS':
      return {
        ...state,
        orders: action.payload
      };

    case 'UPDATE_ORDER':
      return {
        ...state,
        orders: state.orders.map(order =>
          order.id === action.payload.id ? { ...order, ...action.payload.updates } : order
        )
      };

    case 'CLEAR_ORDERS':
      return {
        ...state,
        orders: []
      };

    default:
      return state;
  }
};

const initialState = {
  orders: []
};

export const OrderProvider = ({ children }) => {
  const [state, dispatch] = useReducer(orderReducer, initialState);
  const [loading, setLoading] = useState(false);
  const { currentUser } = useAuth();

  // Load orders from Firestore when logged in (migrate localStorage if present)
  // Also set up real-time listener for order updates
  useEffect(() => {
    let isMounted = true;
    let unsubscribe = null;

    const loadOrders = async () => {
      if (!currentUser) {
        dispatch({ type: 'LOAD_ORDERS', payload: [] });
        return;
      }

      setLoading(true);
      try {
        let localOrders = [];
        try {
          const savedOrders = localStorage.getItem('orderHistory');
          if (savedOrders) {
            const parsedOrders = JSON.parse(savedOrders);
            if (Array.isArray(parsedOrders)) {
              localOrders = parsedOrders;
            }
          }
        } catch {
          localStorage.removeItem('orderHistory');
        }

        const ordersRef = collection(db, 'users', currentUser.uid, 'orders');
        const snapshot = await getDocs(ordersRef);
        const remoteOrders = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data()
        }));

        const mergedMap = new Map();
        [...remoteOrders, ...localOrders].forEach((order) => {
          if (order && order.id) {
            mergedMap.set(order.id, order);
          }
        });
        const merged = Array.from(mergedMap.values()).sort((a, b) => {
          const aDate = new Date(a.date || 0).getTime();
          const bDate = new Date(b.date || 0).getTime();
          return bDate - aDate;
        });

        if (localOrders.length > 0) {
          await Promise.all(localOrders.map((order) => {
            if (!order?.id) return null;
            return setDoc(doc(db, 'users', currentUser.uid, 'orders', order.id), order, { merge: true });
          }));
          localStorage.removeItem('orderHistory');
        }

        if (isMounted) {
          dispatch({ type: 'LOAD_ORDERS', payload: merged });

          // Set up real-time listener after initial load
          unsubscribe = onSnapshot(ordersRef, (snapshot) => {
            const updatedOrders = snapshot.docs.map((docSnap) => ({
              id: docSnap.id,
              ...docSnap.data()
            }));
            const sorted = updatedOrders.sort((a, b) => {
              const aDate = new Date(a.date || 0).getTime();
              const bDate = new Date(b.date || 0).getTime();
              return bDate - aDate;
            });
            if (isMounted) {
              dispatch({ type: 'LOAD_ORDERS', payload: sorted });
            }
          });
        }
      } catch (error) {
        logError(error, { action: 'loadOrdersFirestore', userId: currentUser.uid });
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadOrders();
    return () => {
      isMounted = false;
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [currentUser]);

  const createOrder = async (orderData) => {
    try {
      setLoading(true);

      if (!currentUser) {
        const error = new Error('Please sign in to place orders');
        console.error('❌ Order creation failed: User not logged in');
        throw error;
      }

      const safeItems = Array.isArray(orderData.items) ? orderData.items : [];
      const computedSubtotal = safeItems.reduce((sum, item) => {
        const qty = Number(item.quantity || item.qty || 1);
        const unitPrice = Number(item.price || item.unitPrice || 0);
        return sum + (qty * unitPrice);
      }, 0);
      const computedVatAmount = Number((computedSubtotal * VAT_RATE).toFixed(2));
      const computedShippingFee = Number(orderData.shippingFee || 0);
      const computedTotal = Number((computedSubtotal + computedVatAmount + computedShippingFee).toFixed(2));

      const safeOrderData = { ...(orderData || {}) };
      [
        'invoiceNumber',
        'invoiceIssuedAt',
        'invoiceIssuedBy',
        'receiptNumber',
        'paidAt',
        'paidMarkedBy',
        'paymentStatus',
        'vatRate',
        'vatAmount',
        'subtotal',
        'total'
      ].forEach((key) => {
        if (key in safeOrderData) {
          delete safeOrderData[key];
        }
      });

      const order = {
        id: `ORD-${Date.now()}`,
        date: new Date().toISOString(),
        status: orderData.status || 'pending',
        subtotal: computedSubtotal,
        vatRate: VAT_RATE,
        vatAmount: computedVatAmount,
        shippingFee: computedShippingFee,
        total: computedTotal,
        items: safeItems,
        shippingAddress: orderData.shippingAddress,
        paymentMethod: orderData.paymentMethod,
        paymentReference: orderData.paymentReference || '',
        paymentVerified: orderData.paymentVerified || false,
        paymentStatus: 'unpaid',
        userId: currentUser.uid,
        customerEmail: currentUser.email || orderData.email || '',
        customerName: currentUser.displayName || orderData.shippingAddress?.fullName || '',
        claimedBy: null,
        claimedByName: null,
        claimedAt: null,
        ...safeOrderData
      };

      console.log('📦 Creating order:', {
        id: order.id,
        items: order.items.length
      });

      dispatch({ type: 'ADD_ORDER', payload: order });

      try {
        const userOrderRef = doc(db, 'users', currentUser.uid, 'orders', order.id);
        await setDoc(userOrderRef, order, { merge: true });
      } catch (userOrderError) {
        console.error('❌ Failed to save to user orders collection:', userOrderError);
        try {
          await setDoc(doc(db, 'users', currentUser.uid, 'orders', order.id), order, { merge: true });
        } catch (retryError) {
          console.error('❌ Retry failed for user orders collection:', retryError);
          throw new Error('Could not save order to your account. Please contact support.');
        }
      }

      // Save to global orders collection for admin with retry logic
      try {
        const adminOrderRef = doc(db, 'orders', order.id);
        await setDoc(adminOrderRef, {
          ...order,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        }, { merge: true });
      } catch (adminOrderError) {
        console.error('⚠️ Failed to save to admin orders collection (non-critical):', adminOrderError);
        // Non-critical - user still has their order
      }

      return { success: true, order, message: 'Order created successfully' };
    } catch (error) {
      console.error('💥 ORDER CREATION FAILED:', {
        error: error.message
      });
      logError(error, { action: 'createOrder', userId: currentUser?.uid });
      return { 
        success: false, 
        message: error.message || 'Failed to create order. Please contact support.'
      };
    } finally {
      setLoading(false);
    }
  };

  const getOrderById = (orderId) => {
    return state.orders.find(order => order.id === orderId);
  };

  const getRecentOrders = (limit = 5) => {
    return state.orders.slice(0, limit);
  };

  const clearOrderHistory = async () => {
    try {
      if (!currentUser) {
        dispatch({ type: 'CLEAR_ORDERS' });
        return { success: true, message: 'Order history cleared' };
      }

      const ordersRef = collection(db, 'users', currentUser.uid, 'orders');
      const snapshot = await getDocs(ordersRef);
      await Promise.all(snapshot.docs.map((docSnap) => deleteDoc(docSnap.ref)));
      dispatch({ type: 'CLEAR_ORDERS' });
      return { success: true, message: 'Order history cleared' };
    } catch (error) {
      logError(error, { action: 'clearOrderHistory', userId: currentUser?.uid });
      return { success: false, message: error.message };
    }
  };

  const getOrderStats = () => {
    const totalOrders = state.orders.length;
    const totalSpent = state.orders.reduce((sum, order) => sum + (order.total || 0), 0);
    const pendingOrders = state.orders.filter(o => o.status === 'pending').length;
    const completedOrders = state.orders.filter(o => o.status === 'completed').length;

    return {
      totalOrders,
      totalSpent,
      pendingOrders,
      completedOrders
    };
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      if (!orderId || !newStatus) {
        throw new Error('Order ID and status are required');
      }

      const order = state.orders.find(o => o.id === orderId);
      if (!order) {
        throw new Error('Order not found');
      }

      // Update in admin collection
      const adminOrderRef = doc(db, 'orders', orderId);
      await updateDoc(adminOrderRef, {
        status: newStatus,
        updatedAt: serverTimestamp(),
        statusUpdatedAt: new Date().toISOString()
      });

      // Update in user's order collection
      const userOrderRef = doc(db, 'users', order.userId, 'orders', orderId);
      await updateDoc(userOrderRef, {
        status: newStatus,
        updatedAt: serverTimestamp(),
        statusUpdatedAt: new Date().toISOString()
      });

      // Update local state
      dispatch({
        type: 'UPDATE_ORDER',
        payload: {
          id: orderId,
          updates: {
            status: newStatus,
            statusUpdatedAt: new Date().toISOString()
          }
        }
      });

      console.log(`✓ Order ${orderId} status updated to ${newStatus}`);
      return { success: true, message: `Order status updated to ${newStatus}` };
    } catch (error) {
      logError(error, { action: 'updateOrderStatus', orderId, newStatus });
      return { success: false, message: error.message };
    }
  };

  return (
    <OrderContext.Provider value={{
      orders: state.orders,
      loading,
      createOrder,
      getOrderById,
      getRecentOrders,
      clearOrderHistory,
      getOrderStats,
      updateOrderStatus
    }}>
      {children}
    </OrderContext.Provider>
  );
};

export const useOrders = () => {
  const context = useContext(OrderContext);
  if (!context) {
    throw new Error('useOrders must be used within OrderProvider');
  }
  return context;
};
